import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';
import { TOOL_PATHS } from '../utils/conversionRoutes';
import { BLOG_BASE } from '../utils/blogRoutes';
import { DASHBOARD_BLOG_BASE } from '../components/dashboard/blog/paths';

/**
 * Send a GA page_view on every route change.
 *
 * This is a client-rendered SPA: gtag.js fires exactly one page_view, on the initial
 * document load, and React Router navigations are invisible to it. Without this hook GA
 * had never recorded an in-app navigation — no dashboard route, no tool-to-tool hop, no
 * blog post reached from the index.
 *
 * `send_page_view: false` on the gtag config in public/index.html is the other half:
 * without it the entry page counts twice, once from gtag and once from here.
 */

// Path -> reported pattern. A raw uuid or slug would fragment the report into thousands
// of single-hit rows that no GA report can summarise, so the DYNAMIC segment is replaced
// while the real URL still travels as page_location.
const normalisePath = (pathname) => {
  // Trailing slashes would otherwise report as separate pages.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  // The same 25 articles are served at /blogs/:slug publicly and /guides/:slug inside
  // the dashboard — the two route trees are mutually exclusive, so a signed-in reader
  // never sees /blogs. Reported separately, one article's readership splits in half.
  if (path === DASHBOARD_BLOG_BASE) return BLOG_BASE;
  if (path.startsWith(`${DASHBOARD_BLOG_BASE}/`)) return `${BLOG_BASE}/:slug`;
  if (path.startsWith(`${BLOG_BASE}/`)) return `${BLOG_BASE}/:slug`;

  if (path.startsWith(`${TOOL_PATHS.convert}/`)) return `${TOOL_PATHS.convert}/:conversion`;
  // Legacy /convert/:slug, which redirects but is still entered directly from Google.
  if (path.startsWith('/convert/')) return '/convert/:conversion';

  if (path.startsWith('/workflows/')) {
    return path.endsWith('/editor')
      ? '/workflows/:id/editor'
      : '/workflows/:id';
  }

  return path;
};

const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // document.title is set by react-helmet AFTER render, so reading it synchronously
    // here returns the PREVIOUS route's title. A frame's delay lets the new one land —
    // otherwise every page_view is attributed to the page before it.
    const id = window.requestAnimationFrame(() => {
      trackPageView({
        path: normalisePath(location.pathname),
        location: window.location.href,
        title: document.title,
      });
    });
    return () => window.cancelAnimationFrame(id);
    // search is included: ?tab=topic on /workflows is a different screen.
  }, [location.pathname, location.search]);
};

export { normalisePath };
export default usePageTracking;
